import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'click',
  'scroll',
  'touchstart',
  'wheel',
];

const SYSTEM_PATH_PREFIX = '/dashboard/system';
const WARNING_THRESHOLD_MS = 5 * 60 * 1000;
const TICK_INTERVAL_MS = 1000;

@Injectable({ providedIn: 'root' })
export class SystemAutoRefreshService {
  private readonly timeoutMs: number;
  private timer?: ReturnType<typeof setTimeout>;
  private warningTimer?: ReturnType<typeof setTimeout>;
  private ticker?: ReturnType<typeof setInterval>;
  private active = false;
  private startedAt = 0;
  private boundReset?: () => void;
  private boundVisibility?: () => void;
  private pendingRefreshOnReturn = false;

  private remainingSubject = new BehaviorSubject<number | null>(null);
  readonly remaining$: Observable<number | null> =
    this.remainingSubject.asObservable();

  constructor(private router: Router) {
    const minutes = Number(
      (environment as any).SYSTEM_AUTO_REFRESH_MINUTES,
    );
    const sanitized = Number.isFinite(minutes) && minutes > 0 ? minutes : 30;
    this.timeoutMs = sanitized * 60 * 1000;

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncFromUrl());

    this.syncFromUrl();
  }

  private syncFromUrl() {
    const currentUrl = this.router.url || '';
    const onSystem = currentUrl.startsWith(SYSTEM_PATH_PREFIX);
    if (onSystem && !this.active) {
      this.activate();
    } else if (!onSystem && this.active) {
      this.deactivate();
    } else if (onSystem && this.active) {
      this.resetTimer();
    }
  }

  private activate() {
    if (this.active) return;
    this.active = true;
    this.boundReset = () => this.resetTimer();
    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, this.boundReset!, { passive: true }),
    );

    this.boundVisibility = () => this.onVisibilityChange();
    document.addEventListener('visibilitychange', this.boundVisibility);

    this.resetTimer();
  }

  private deactivate() {
    if (!this.active) return;
    this.active = false;
    if (this.boundReset) {
      ACTIVITY_EVENTS.forEach((evt) =>
        document.removeEventListener(evt, this.boundReset!),
      );
      this.boundReset = undefined;
    }
    if (this.boundVisibility) {
      document.removeEventListener('visibilitychange', this.boundVisibility);
      this.boundVisibility = undefined;
    }
    this.clearTimer();
    this.clearWarningTimer();
    this.clearTicker();
    this.pendingRefreshOnReturn = false;
    this.remainingSubject.next(null);
  }

  private resetTimer() {
    this.clearTimer();
    this.clearWarningTimer();
    this.clearTicker();
    this.remainingSubject.next(null);

    this.startedAt = Date.now();
    this.timer = setTimeout(() => this.fireRefresh(), this.timeoutMs);

    const warningDelay = this.timeoutMs - WARNING_THRESHOLD_MS;
    if (warningDelay > 0) {
      this.warningTimer = setTimeout(
        () => this.startTicking(),
        warningDelay,
      );
    } else {
      this.startTicking();
    }
  }

  private startTicking() {
    this.clearTicker();
    this.emitRemaining();
    this.ticker = setInterval(() => this.emitRemaining(), TICK_INTERVAL_MS);
  }

  private emitRemaining() {
    const remaining = this.timeoutMs - (Date.now() - this.startedAt);
    if (remaining <= 0) {
      this.remainingSubject.next(0);
      this.clearTicker();
      return;
    }
    if (remaining <= WARNING_THRESHOLD_MS) {
      this.remainingSubject.next(remaining);
    } else {
      this.remainingSubject.next(null);
    }
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  private clearWarningTimer() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = undefined;
    }
  }

  private clearTicker() {
    if (this.ticker) {
      clearInterval(this.ticker);
      this.ticker = undefined;
    }
  }

  private fireRefresh() {
    if (typeof document !== 'undefined' && document.hidden) {
      this.pendingRefreshOnReturn = true;
      return;
    }
    this.doReload();
  }

  private onVisibilityChange() {
    if (
      typeof document !== 'undefined' &&
      !document.hidden &&
      this.pendingRefreshOnReturn
    ) {
      this.pendingRefreshOnReturn = false;
      this.doReload();
    }
  }

  private doReload() {
    try {
      window.location.reload();
    } catch {

    }
  }
}
